import barba from '@barba/core';
import {gsap} from 'gsap';
import Modals from './modals.js';
import Share from './share.js';
import Accordion from './accordion.js';
import Navigation from './navigation.js';
import Header from './header.js';
import Toc from './toc.js';
import GTM from './gtm.js';
import Parallax from './parallax.js';
import Cookies from './cookies.js';
import BaseLesson from "./base-lesson";
import Swiper from "./swiper";
import Test from "./test";

/**
 * Creates a delay that will be used to finish page transitions.
 * Yes, this function is created solely to make the website slower, #animationlife.
 *
 * @param milliseconds - Amount of time delay in milliseconds. Defaults to 2000.
 * @returns {Promise<unknown>}
 */
function delay(milliseconds) {
  milliseconds = milliseconds || 2000;

  return new Promise( (done) => {
    setTimeout( () => {
      done();
    }, milliseconds );
  } );
}

/**
 * Updates the body classes after a page transition.
 *
 * @param data - The data given by barba so we can (ab)use it
 */
function updateBodyClasses(data) {
  let parser = new DOMParser(),
    htmlDoc = parser.parseFromString( data.next.html.replace( /(<\/?)body( .+?)?>/gi, '$1notbody$2>', data.next.html ), 'text/html' ),
    bodyClasses = htmlDoc.querySelector( 'notbody' ).getAttribute( 'class' );

  document.body.setAttribute( 'class', bodyClasses );
}

/**
 * Updates the admin bar after a barba page transition.
 *
 * @param data - The data given by barba so we can (ab)use it
 */
function updateAdminBar(data) {
  let parser = new DOMParser(),
    htmlDoc = parser.parseFromString( data.next.html.replace( /(<\/?)body( .+?)?>/gi, '$1notbody$2>', data.next.html ), 'text/html' );

  document.getElementById( 'wpadminbar' ).innerHTML = htmlDoc.querySelector( '#wpadminbar' ).innerHTML;
}

function updateHeader(data) {
  let parser = new DOMParser(),
    htmlDoc = parser.parseFromString( data.next.html.replace( /(<\/?)body( .+?)?>/gi, '$1notbody$2>', data.next.html ), 'text/html' ),
    header = htmlDoc.querySelector( '.header' );

  document.querySelector( '.header' ).innerHTML = header.innerHTML;
}

/**
 * Updates the logo colour after a barba page transition.
 *
 * @param data - The data given by barba so we can (ab)use it
 */
function updateLogoColour(data) {
  let parser = new DOMParser(),
    htmlDoc = parser.parseFromString( data.next.html.replace( /(<\/?)body( .+?)?>/gi, '$1notbody$2>', data.next.html ), 'text/html' ),
    bodyClasses = htmlDoc.querySelector( 'notbody' ).getAttribute( 'class' ),
    logoWrapper = document.querySelector( '.header__logo img' );
}

function getNewBodyClasses(data) {
  let parser = new DOMParser(),
    htmlDoc = parser.parseFromString( data.next.html.replace( /(<\/?)body( .+?)?>/gi, '$1notbody$2>', data.next.html ), 'text/html' ),
    bodyClasses = htmlDoc.querySelector( 'notbody' ).getAttribute( 'class' );

  return bodyClasses;
}

/**
 * Define the timeline for the page transition
 */
function pageTransition(colour) {
  const tl = gsap.timeline(),
    transitionClass = '.transition--' + colour;

  if (tl && document.querySelector( transitionClass )) {
    tl.set( transitionClass, {opacity: 0, scaleY: 0} );
    tl.to( transitionClass, {
      duration: 1,
      opacity: 1,
      scaleY: 1,
      ease: 'expo.out',
    } );
  }
}

function hidePageTransition() {
  const tl = gsap.timeline();

  if (tl && document.querySelector( '.transition' )) {
    tl.to( '.transition', {
      duration: 1,
      opacity: 0,
      ease: 'expo.out',
    } ).to( '.transition', {
      duration: .01,
      scaleY: 0,
      ease: 'expo.out',
    } );
  }
}

function animateHeader() {
  const tl = gsap.timeline();

  if (tl && document.querySelector( '.header' )) {
    tl.set( ['.header'], {
      opacity: 0,
      y: -25,
    } );

    tl.to( '.header', {
      duration: 1.5,
      opacity: 1,
      y: 0,
      ease: 'expo.out',
    } );
  }
}

function animateHeaderOut() {
  const tl = gsap.timeline();

  if (tl && document.querySelector( '.header' )) {
    tl
      // Fade in header
      .to( '.header', {
        duration: 1.5,
        opacity: 0,
        y: -25,
        ease: 'expo.out',
      } )
      // Hide submenu just to be sure
      .to( '.nav-main__submenu', {
        height: 0,
      }, '>-1.5' );
  }
}

function animateBanner() {
  const tl = gsap.timeline();

  if (tl) {

    if (document.querySelector( '[data-accordion]' )) {
      tl.set( ['[data-accordion]'], {opacity: 0, y: 50} );
    }

    if (document.querySelector( '[data-banner-chip]' )) {
      tl.set( ['[data-banner-chip]'], {opacity: 0, x: -75} );
    }

    if (document.querySelector( '[data-banner-title]' )) {
      tl.set( ['[data-banner-title]'], {opacity: 0, x: -75} );
    }

    if (document.querySelector( '[data-banner-intro]' )) {
      tl.set( ['[data-banner-intro]'], {opacity: 0, x: -75} );
    }

    if (document.querySelector( '[data-banner-btns]' )) {
      tl.set( ['[data-banner-btns]'], {opacity: 0, x: -75} );
    }

    if (document.querySelector( '[data-banner-img]' )) {
      tl.set( ['[data-banner-img]'], {opacity: 0, x: -75} );
    }

    tl
      // Move up accordion
      .to( '[data-accordion]', {
        duration: 1.5,
        opacity: 1,
        y: 0,
        ease: 'expo.out',
      } )
      // Slide in chip
      .to( '[data-banner-chip]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-0.5' )
      // Slide in title
      .to( '[data-banner-title]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-1.5' )
      // Slide in intro
      .to( '[data-banner-intro]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-0.75' )
      // Slide in banner buttons
      .to( '[data-banner-btns]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-0.75' )
      // Slide in banner image
      .to( '[data-banner-img]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-1.25' );
  }
}

function animateLessonPage() {
  const tl = gsap.timeline();

  if (tl && document.querySelector( '[data-lesson-blurred]' ) && document.querySelector( '[data-lesson-title]' )) {
    tl.set( [
      '[data-lesson-blurred]',
    ], {
      opacity: 0,
      y: -200,
    } );

    tl.set( [
      '[data-lesson-pill]',
      '[data-title-alert-box]',
      '[data-lesson-title]',
      '[data-lesson-content]',
      '[data-lesson-checkboxes]',
      '[data-title-actions]',
      '[data-actions]',
      '[data-lesson-btns]',
      '[data-lesson-more-info]',
      '[data-lesson-share]',
      '[data-lesson-graphic]',
    ], {
      opacity: 0,
      x: -75,
    } );

    tl.set( [
      '[data-lesson-side-prev]',
      '[data-lesson-side-archive]',
      '[data-lesson-side-next]',
    ], {
      opacity: 0,
      x: 100,
    } );

    tl
      // Blurred image down
      .to( '[data-lesson-blurred]', {
        duration: 1,
        opacity: .35,
        y: 0,
        ease: 'expo.out',
      } )
      // Slide pill in
      .to( '[data-lesson-pill]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-.75' )
      // Slide title in
      .to( '[data-lesson-title]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-.75' )
      // Slide alert box title in
      .to( '[data-title-alert-box]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-.75' )
      // Slide content in
      .to( '[data-lesson-content]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-.75' )
      // Slide side graphic in
      .to( '[data-lesson-graphic]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-1' )
      // Show checkboxes
      .to( '[data-lesson-checkboxes]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-.5' )
      // Show actions title
      .to( '[data-title-actions]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-.5' )
      // Show actions
      .to( '[data-actions]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-.5' )
      // Show buttons
      .to( '[data-lesson-btns]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-.75' )
      // Show more info
      .to( '[data-lesson-more-info]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-.75' )
      // Show share
      .to( '[data-lesson-share]', {
        duration: 1,
        opacity: 1,
        x: 0,
        ease: 'expo.out',
      }, '>-.75' );
  }
}

function animateArchiveCards() {
  const tl = gsap.timeline();

  if (tl && document.querySelector( '.card--archive' )) {
    tl.set( '.card--archive', {
      opacity: 0,
      y: -100,
    } );

    tl.to( '.card--archive', {
      y: 0,
      opacity: 1,
      stagger: 0.15,
      ease: 'expo.out',
    } );
  }
}

function animateBaseLesson() {
  const tl = gsap.timeline();

  if (tl && document.querySelector( '.base-lesson-works' )) {
    tl.set( '.base-lesson-dragger', {
      opacity: 0,
    } );

    tl.set( '.base-lesson-works', {
      width: '100vw',
    } );

    tl.set( '.base-lesson-recognizes', {
      opacity: 0,
    } );

    tl
      // Show dragger
      .to( '.base-lesson-dragger', {
        opacity: 1,
        ease: 'expo.out',
      } )
      // Show blue part
      .to( '.base-lesson-recognizes', {
        opacity: 1,
      } )
      // Shrink pink part again
      .to( '.base-lesson-works', {
        width: 'auto',
      } );
  }
}

function animateTest() {
  const tl = gsap.timeline();

  tl.set( '[data-quiz-main]', {
    display: 'none'
  } );

  tl.set( '[data-lesson-images]', {
    opacity: 0,
  } );

  tl.set( '[data-quiz-splash]', {
    y: '35%'
  } );

  tl.set( '[data-quiz-splash-title-span]', {
    y: '100%'
  } );

  tl.set( '[data-quiz-splash-answer]', {
    scale: 1.1
  } );

  tl.set( '[data-quiz-splash-answer]:nth-child(1)', {
    x: '-120%',
    rotate: '-15deg'
  } );

  tl.set( '[data-quiz-splash-answer]:nth-child(2)', {
    x: '120%',
    rotate: '15deg'
  } );

  tl.set( '[data-quiz-splash-answer]:nth-child(3)', {
    x: '-25%',
    y: '175%',
    rotate: '-15deg'
  } );

  tl.set( '[data-quiz-splash-answer]:nth-child(4)', {
    x: '25%',
    y: '175%',
    rotate: '15deg'
  } );

  tl.set( '[data-quiz-main-progress]', {
    y: '-100%'
  } );

  tl.set( '[data-quiz-main-progress]', {
    opacity: 0,
    y: '-100%'
  } );

  tl.set( '[data-quiz-main-pill]', {
    opacity: 0,
    y: '-100%'
  } );

  tl.set( '[data-quiz-main-pill]', {
    opacity: 0,
    y: '-100%'
  } );

  tl.set( '[data-quiz-main-title-span]', {
    y: '100%'
  } );

  tl
    // Show splash
    .to( '.quiz-splash', {
      autoAlpha: 1,
      display: 'block'
    } )
    // Show title
    .to( '[data-quiz-splash-title-span]', {
      y: '0%',
      duration: .75,
      ease: 'expo.out',
    } )
    // Tease answer 1
    .to( '[data-quiz-splash-answer]:nth-child(1)', {
      x: '-100%',
      ease: "back.out(1.7)"
    }, '>-0.75' )
    // Tease answer 2
    .to( '[data-quiz-splash-answer]:nth-child(2)', {
      x: '100%',
      ease: "back.out(1.7)"
    }, '>-0.75' )
    // Tease answer 3
    .to( '[data-quiz-splash-answer]:nth-child(3)', {
      y: '30%',
      ease: "back.out(1.7)"
    }, '>-0.75' )
    // Tease answer 4
    .to( '[data-quiz-splash-answer]:nth-child(4)', {
      y: '30%',
      ease: "back.out(1.7)"
    }, '>-0.75' )
    // Move up splash
    .to( '[data-quiz-splash]', {
      y: '0%',
    } )
    // Move answer 1 to middle
    .to( '[data-quiz-splash-answer]:nth-child(1)', {
      x: '50%',
      rotate: '-2deg',
      ease: "back.out(1.7)"
    }, '>-0.5' )
    // Move answer 2 to middle
    .to( '[data-quiz-splash-answer]:nth-child(2)', {
      x: '-50%',
      rotate: '3deg',
      ease: "back.out(1.7)"
    }, '>-0.5' )
    // Move answer 3 to middle
    .to( '[data-quiz-splash-answer]:nth-child(3)', {
      x: '50%',
      y: '-100%',
      rotate: '-5deg',
      ease: "back.out(1.7)"
    }, '>-0.5' )
    // Move answer 4 to middle
    .to( '[data-quiz-splash-answer]:nth-child(4)', {
      x: '-50%',
      y: '-100%',
      rotate: '2deg',
      ease: "back.out(1.7)"
    }, '>-0.5' )
    // Hide title span
    .to( '[data-quiz-splash-title-span]', {
      y: '-100%',
    }, '>+0.5' )
    // Scale down all answers
    .to( '[data-quiz-splash-answer]', {
      scale: 1,
    } )
    // Show answers
    .to( '[data-quiz-splash-answer]', {
      x: '0%',
      y: '0%',
      rotate: '0deg',
      duration: .75,
      ease: "back.out(1.7)",
    } )
    // Hide splash
    .to( '[data-quiz-splash]', {
      display: 'none'
    } )
    // Show real test
    .to( '[data-quiz-main]', {
      display: 'block'
    } )
    // Show progress
    .to( '[data-quiz-main-progress]', {
      opacity: 1,
      y: '0%',
      ease: "back.out(2.7)",
    }, '>-.25' )
    // Show pill
    .to( '[data-quiz-main-pill]', {
      opacity: 1,
      y: '0%',
      ease: "back.out(2.7)",
    }, '>-.25' )
    // Show title
    .to( '[data-quiz-main-title-span]', {
      y: '0%',
      ease: "back.out(2.7)",
    }, '>-.25' )
    // Show images
    .to( '[data-lesson-images]', {
      opacity: 1,
    } );
}

function animateQuizResults() {
  const tl = gsap.timeline();
  const wrongAnswers = document.querySelectorAll( '[data-quiz-result-wrong-answer]' );

  if (tl && document.querySelector( '[data-quiz-result-title]' )) {
    tl.set( [
      '[data-quiz-result-title]',
      '[data-quiz-result-text]',
      '[data-quiz-result-wrong-answer]',
      '[data-quiz-result-share]',
      '[data-result-ctas]'
    ], {
      opacity: 0,
      x: -75,
    } );

    tl
      // Show title
      .to( '[data-quiz-result-title]', {
        opacity: 1,
        x: 0,
      } )
      // Show text
      .to( '[data-quiz-result-text]', {
        opacity: 1,
        x: 0,
      } );

    if (wrongAnswers.length) {
      tl
        // Show wrong answers
        .to( '[data-quiz-result-wrong-answer]', {
          opacity: 1,
          x: 0,
          stagger: 0.15,
        } );
    }

    // Show CTAs
    tl.to( '[data-result-ctas]', {
      opacity: 1,
      x: 0,
    } )
    // Show share
    tl.to( '[data-quiz-result-share]', {
      opacity: 1,
      x: 0,
    } );
  }
}

export default {
  init() {
    if (window.innerWidth > 1200) {
      /** Initialize barba JS */
      barba.init( {

        /** Prevent links from doing a page transition */
        prevent: ({el}) => el.classList && el.classList.contains( 'ab-item' ),

        /** Transitions definition */
        transitions: [
          {
            /** Page is being left: start of transition */
            async leave(data) {
              const trigger = data.trigger;
              const done = this.async();

              if (trigger.hasAttribute( 'data-page-transition-colour' )) {
                pageTransition( trigger.getAttribute( 'data-page-transition-colour' ) );
              }

              animateHeaderOut();
              await delay( 1250 );
              done();
            },

            /** Page is being entered, starts after leave() */
            async enter(data) {
              const bodyClasses = getNewBodyClasses( data );

              window.scrollTo( 0, 0 );

              animateHeader();
              hidePageTransition();
              animateBanner();

              if (bodyClasses.includes( 'single-lesson' )) {
                animateLessonPage();
              }

              if (bodyClasses.includes( 'post-type-archive' )) {
                animateArchiveCards();
              }

              if (bodyClasses.includes( 'page-template-template-base-lesson' )) {
                animateBaseLesson();
              }

              if (bodyClasses.includes( 'page-template-template-quiz' )) {
                animateTest();
              }

              if (bodyClasses.includes( 'page-template-template-quiz-results' )) {
                animateQuizResults();
              }
            },

            /** First page load */
            async once(data) {
              updateLogoColour( data );
              animateHeader();
              animateBanner();
              animateLessonPage();
              animateArchiveCards();
              animateTest();
              animateQuizResults();
            },

            /** After page transition is done */
            async after(data) {
              updateBodyClasses( data );
              updateLogoColour( data );
              updateHeader( data );

              // Re-init all the JS components
              Modals.init();
              Share.init();
              Accordion.init();
              Navigation.init();
              Header.init();
              Toc.init();
              GTM.init();
              Parallax.init();
              Cookies.init();
              BaseLesson.init();
              Swiper.init();
              Test.init();

              if (document.getElementById( 'wpadminbar' )) {
                updateAdminBar( data );
              }
            },
          },
        ],
      } );
    }
  },
};